import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE, getMemberId, getToken, wsUrl } from '../lib/api';
import type { Member, Role } from '../lib/api';
/* Card tPPB3ozS4YrK — the throttle window is a shared constant so the client's
   floor and the room's floor are the same number, read from one place. */
import { TYPING_MIN_INTERVAL_MS } from '../../../shared/types';
/* H7 (card ESidDNa3mmaP): a send the socket cannot take now says so, so the
   composer can put the draft back instead of losing it silently. */
import { reportActionFailure } from '../lib/actionFeedback';

export interface ThreadMessage {
  seq: number;
  kind?: string;
  text: string;
  fromMemberId?: string | null;
  /** Epoch ms the DO stamped the row — drives the day separators and the
   *  7-day hydration window (card 4ynbWszGdqUd). Always present on history
   *  rows; live frames carry it too. */
  ts?: number;
  /** Only on a game-round card: which round it belongs to. */
  roundId?: string;
  /** Echo correlation id — carried on history rows so a reconnect can
   * confirm an in-flight send whose `ack` died with the old socket. */
  clientMsgId?: string;
}

export interface LocalEcho {
  clientMsgId: string;
  text: string;
}

/** A send the socket could not take (card 4FFux4skKvYw). It keeps its
 *  clientMsgId, so the eventual send is the SAME frame the composer would
 *  have posted: one row, one ack, no duplicate. */
export interface QueuedSend {
  clientMsgId: string;
  text: string;
  wantsAgent: boolean;
}

export interface ThreadState {
  messages: ThreadMessage[];
  echoes: LocalEcho[];
  /** Sends parked while the socket was down, in send order. Flushed on
   *  reconnect; a row here is the durable record that the words were kept. */
  queued: QueuedSend[];
  /** True while the socket is open — drives the header connection dot. */
  connected: boolean;
  /** True once the first history frame lands, so the skeleton has an end. */
  historyLoaded: boolean;
  /** True when the server holds messages older than the window currently
   *  loaded — the "Show earlier messages" affordance reads this (card
   *  4ynbWszGdqUd). Set from the history frame's `hasMore`. */
  hasOlder: boolean;
}

/** How far back a fresh open hydrates (card 4ynbWszGdqUd): the thread loads
 *  the last 7 days on open; anything older waits behind the expand call. */
export const THREAD_WINDOW_DAYS = 7;

/** How much history one "jump to this message" pulls (card 2sjo3uNvaRDr).
 *  `before` is exclusive, so the page requested ends AT the target and this
 *  is the depth below it — enough surrounding rows that the message lands in
 *  context rather than alone at the top of an empty stream. */
export const HISTORY_PAGE_TO_TARGET = 60;

/** The exact frame a send posts — shared by the live path, the reconnect
 *  flush and the Retry affordance so all three are byte-identical
 *  (card 4FFux4skKvYw: same clientMsgId, so the room sees ONE message).
 *  Module scope on purpose: it reads nothing from the hook's state, and three
 *  copies of this shape is how an exactly-once bug gets in. */
function sendFrame(q: QueuedSend): string {
  return JSON.stringify(
    q.wantsAgent
      ? { v: 1, t: 'send', clientMsgId: q.clientMsgId, text: q.text, wantsAgent: true }
      : { v: 1, t: 'send', clientMsgId: q.clientMsgId, text: q.text },
  );
}

/**
 * Couple WebSocket: history, live messages, ack'd local echo, auto-reconnect.
 * Replaces the `connectWebSocket` / `renderHistory` / `appendMessage` /
 * `sendWsText` block from `main.ts`.
 *
 * Two deliberate changes:
 *  - messages are kept sorted by `seq`. The old code appended each older
 *    history page after the newer one, so a thread longer than one page
 *    rendered out of order.
 *  - rendering is declarative, so there is no `innerHTML` + `escapeHtml` pair
 *    to keep in sync — React escapes text nodes by default.
 */
export function useThread(opts: {
  token: string | null;
  members: Member[];
  onInvalidToken: () => void;
}) {
  const { token, members, onInvalidToken } = opts;
  const [state, setState] = useState<ThreadState>({ messages: [], echoes: [], queued: [], connected: false, historyLoaded: false, hasOlder: false });
  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef<number | null>(null);
  const seenRef = useRef<Set<number>>(new Set());
  /* Queued sends, readable from the socket handlers without re-binding them
     (card 4FFux4skKvYw) — the reconnect flush runs inside onopen, which was
     created before this state exists in the closure. */
  const queuedRef = useRef<QueuedSend[]>([]);
  queuedRef.current = state.queued;
  /* Oldest loaded seq, read by loadOlder to page backwards (card 4ynbWszGdqUd).
     Kept in a ref so the callback does not re-create on every message. */
  const messagesRef = useRef<ThreadMessage[]>([]);
  messagesRef.current = state.messages;
  const membersRef = useRef(members);
  membersRef.current = members;
  const invalidRef = useRef(onInvalidToken);
  invalidRef.current = onInvalidToken;

  const appendMessage = useCallback((msg: ThreadMessage) => {
    if (typeof msg.seq === 'number') {
      if (seenRef.current.has(msg.seq)) return;
      seenRef.current.add(msg.seq);
    }
    setState(s => ({ ...s, messages: [...s.messages, msg] }));
  }, []);

  /* Send everything parked while the socket was down, in order, then move each
     row from `queued` to `echoes` so it reads as in-flight until the ack. */
  const flushQueue = useCallback((ws: WebSocket) => {
    const pending = queuedRef.current;
    if (!pending.length) return;
    const sent: QueuedSend[] = [];
    for (const q of pending) {
      try {
        ws.send(sendFrame(q));
        sent.push(q);
      } catch {
        // Socket died mid-flush: leave this and everything after it queued.
        break;
      }
    }
    if (!sent.length) return;
    const ids = new Set(sent.map(q => q.clientMsgId));
    setState(s => ({
      ...s,
      queued: s.queued.filter(q => !ids.has(q.clientMsgId)),
      echoes: [...s.echoes, ...sent.filter(q => !s.echoes.some(e => e.clientMsgId === q.clientMsgId)).map(q => ({ clientMsgId: q.clientMsgId, text: q.text }))],
    }));
  }, []);

  /* Retry from the queued row or the toast. Offline is NOT an error: the row
     simply stays queued and nothing else is said (no spam, no second row). */
  const retryQueued = useCallback((clientMsgId: string) => {
    const item = queuedRef.current.find(q => q.clientMsgId === clientMsgId);
    const ws = wsRef.current;
    if (!item || !ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      flushQueue(ws);
    } catch {
      return;
    }
  }, [flushQueue]);

  /* Park a send the socket could not take. One toast says so, with Retry on
     it; the queued row in the stream is the durable record. */
  const queueSend = useCallback(
    (q: QueuedSend) => {
      setState(s => (s.queued.some(x => x.clientMsgId === q.clientMsgId) ? s : { ...s, queued: [...s.queued, q] }));
      reportActionFailure('send that message', {
        message: 'Not sent — queued. It will go when the thread reconnects.',
        ttlMs: 4000,
        retry: { label: 'Retry', run: () => retryQueued(q.clientMsgId) },
      });
    },
    [retryQueued],
  );

  const send = useCallback((text: string, wantsAgent = false): boolean => {
    const ws = wsRef.current;
    const clientMsgId = crypto.randomUUID();
    const frame: QueuedSend = { clientMsgId, text, wantsAgent };
    // H7 kept its promise, and card 4FFux4skKvYw lifts it: a send the socket
    // cannot take is QUEUED, not bounced back into the composer. The words
    // come back as a pending row the user can see, retry, or simply wait out —
    // "sent", "queued" or "rejected with a reason" are the only outcomes, and
    // this is the middle one, so the composer stays empty.
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.error('[Pear] send queued — socket not open', ws ? ws.readyState : 'no socket');
      queueSend(frame);
      return true;
    }
    setState(s => ({ ...s, echoes: [...s.echoes, { clientMsgId, text }] }));
    // wantsAgent carries the composer chip (invoke path, card hphiEFLMogbg).
    // Omitted entirely when false so the default frame stays byte-identical
    // to what shipped before.
    try {
      ws.send(sendFrame(frame));
    } catch (e) {
      // The optimistic echo must not sit there claiming "sending" forever —
      // hand it to the queue with the SAME clientMsgId so the retry is one
      // message, not a second copy.
      console.error('[Pear] ws send failed', e);
      setState(s => ({ ...s, echoes: s.echoes.filter(x => x.clientMsgId !== clientMsgId) }));
      queueSend(frame);
      return true;
    }
    return true;
  }, [queueSend]);

  /* Delete your own message (card — msg-remove). Sends the seq over the live
     socket; the DO refuses anything that is not a self-authored text row, so a
     no-op here is quiet by design. Optimistic on the client: the row is dropped
     from local state immediately and the broadcast to the partner removes it
     there on the same connection, so the sender does not wait on the echo
     (msg-remove handling in the merge already filters by seq). */
  const removeMessage = useCallback((seq: number): void => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.error('[Pear] delete dropped — socket not open', ws ? ws.readyState : 'no socket');
      reportActionFailure('delete that message');
      return;
    }
    try {
      ws.send(JSON.stringify({ v: 1, t: 'msg-remove', seq }));
    } catch (e) {
      console.error('[Pear] ws delete failed', e);
      reportActionFailure('delete that message');
      return;
    }
    setState(s => ({ ...s, messages: s.messages.filter(m => m.seq !== seq) }));
  }, []);

  /* Card tPPB3ozS4YrK — "I am typing", at most one frame every two seconds.
     The window lives here so every caller inherits it, and the room enforces
     the same TYPING_MIN_INTERVAL_MS on the way out — a frame that slips past
     this ref is dropped there rather than relayed twice. Never carries the
     draft; a silent socket is simply no frame, not an error worth a toast. */
  const lastTypingRef = useRef(0);
  const typing = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (now - lastTypingRef.current < TYPING_MIN_INTERVAL_MS) return;
    lastTypingRef.current = now;
    try {
      ws.send(JSON.stringify({ v: 1, t: 'typing' }));
    } catch {
      /* socket died between the check and the send — the hint is not worth
         reporting: the partner simply does not see it, and the next keystroke
         after a reconnect tries again. */
    }
  }, []);

  /* Pull the next-older page, freely into the past (no window). Wired to the
     "Show earlier messages" affordance (card 4ynbWszGdqUd). Guarded so a
     double-tap does not fire two identical pages; the merge in the history
     frame de-dupes by seq anyway. */
  const loadOlderBusy = useRef(false);
  const loadOlder = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || loadOlderBusy.current) return;
    const msgs = messagesRef.current;
    if (!msgs.length) return;
    const oldest = msgs.reduce((n, m) => Math.min(n, m.seq), Infinity);
    if (!Number.isFinite(oldest) || oldest <= 1) {
      setState(s => (s.hasOlder ? { ...s, hasOlder: false } : s));
      return;
    }
    loadOlderBusy.current = true;
    ws.send(JSON.stringify({ v: 1, t: 'history', before: oldest, limit: 200 }));
    // Release the guard after a beat — the merge is seq-deduped regardless.
    window.setTimeout(() => { loadOlderBusy.current = false; }, 400);
  }, []);

  const reset = useCallback(() => {
    seenRef.current = new Set();
    setState({ messages: [], echoes: [], queued: [], connected: false, historyLoaded: false, hasOlder: false });
  }, []);

  /* Archive search (card 2sjo3uNvaRDr). The window only ever narrows the
     fresh open, so the only way to reach October is a server-side read — this
     is it. Results are NOT merged into the stream: a search result is a
     pointer, and dumping old bubbles into a 7-day view would make the window
     a lie. The caller shows them and asks for the page that holds one. */
  const search = useCallback(async (query: string): Promise<ThreadMessage[] | null> => {
    const q = query.trim();
    if (q.length < 2) return [];
    const token = getToken();
    if (!token) return null;
    try {
      const res = await fetch(`${API_BASE}/api/chat/search?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { messages?: ThreadMessage[] };
      return Array.isArray(data.messages) ? data.messages : [];
    } catch {
      return null;
    }
  }, []);

  /* Page history so `seq` is loaded, then hand back whether it landed. One
     request: `before` is exclusive, so asking for the page ENDING at seq
     (before = seq + 1) returns seq itself as the newest row of that page. The
     merge in the history frame de-dupes by seq, so a target already on screen
     costs nothing. Returns false when the socket could not take it — the
     caller keeps its result list rather than pretending the jump worked. */
  const loadToSeq = useCallback((seq: number): boolean => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    if (!Number.isFinite(seq) || seq < 1) return false;
    ws.send(JSON.stringify({ v: 1, t: 'history', before: Math.floor(seq) + 1, limit: HISTORY_PAGE_TO_TARGET }));
    return true;
  }, []);

  useEffect(() => {
    if (!token) return;

    let closed = false;
    const connect = () => {
      if (closed) return;
      if (wsRef.current && wsRef.current.readyState <= WebSocket.CONNECTING) return;
      const ws = new WebSocket(wsUrl(token));
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ v: 1, t: 'hello', token }));
        setState(s => ({ ...s, connected: true }));
        // Card 4FFux4skKvYw: whatever was typed while the room was down goes
        // out now, in order, with the clientMsgId it was queued with — so the
        // ack confirms it as one message and the reconnect produces no
        // duplicate and no lost text.
        flushQueue(ws);
        // A reconnect that happened while the tab stayed foregrounded and
        // online fires NO browser event, so the panels fed by the socket would
        // otherwise stay as stale as the moment the drop cut them off. The
        // consumers (Guess Me round + history) refetch on this (sol review
        // 2026-10-19).
        window.dispatchEvent(new CustomEvent('pear:reconnected'));
      };

      ws.onmessage = (ev: MessageEvent) => {
        let msg: any;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        switch (msg.t) {
          case 'welcome':
            /* A fresh open hydrates only the last THREAD_WINDOW_DAYS days (card
               4ynbWszGdqUd) — the old code requested the newest page and then
               recursively walked `before:` all the way back, so a long thread
               pulled its entire history off the DO on every open. The window
               bounds that; anything older waits behind loadOlder(). */
            ws.send(JSON.stringify({ v: 1, t: 'history', limit: 200, sinceDays: THREAD_WINDOW_DAYS }));
            // The couple completed while THIS device had no socket, so the
            // room's `paired` frame never reached it — the welcome repeats
            // the news (card 2NuQ8dd2JMqs). useSession decides whether that
            // is news here: it knows whether this device has both members.
            if (msg.paired) window.dispatchEvent(new CustomEvent('pear:paired'));
            break;
          case 'history': {
            const page: ThreadMessage[] = msg.messages || [];
            setState(s => (s.historyLoaded ? s : { ...s, historyLoaded: true }));
            // Oldest pages are requested with `before:`, so merge then sort —
            // appending in arrival order renders old messages last.
            //
            // The merge must ALSO filter. This frame is requested on every
            // reconnect (the 2s retry loop opens a fresh socket, the server
            // answers `welcome`, and the client asks for the last 50 again),
            // so a page that is appended raw is appended AGAIN on every socket
            // drop — one extra copy of the whole page per reconnect, for ever.
            // That is the "thread entries repeat indefinitely" bug (card
            // QuKQzOvoE40f). The seen-set used to guard only live `msg`
            // frames; it now guards the merge too, and a `msg` that raced
            // ahead of its own history page no longer renders twice either.
            if (page.length) {
              const fresh = page.filter(m => !seenRef.current.has(m.seq));
              for (const m of fresh) seenRef.current.add(m.seq);
              if (fresh.length) {
                setState(s => ({
                  ...s,
                  messages: [...s.messages, ...fresh].sort((a, b) => a.seq - b.seq),
                }));
              }
              // An echo whose `ack` died with the old socket: the send DID
              // land — it is in this history page — so the optimistic bubble
              // is a ghost sitting next to the real message. Confirm it
              // against the page's clientMsgId and drop it, exactly what the
              // ack would have done. Without this, one send renders twice
              // after every mid-flight drop and the copies never clear
              // (card PuxpXa9bnsFA).
              const landed = new Set(page.map(m => m.clientMsgId).filter(Boolean));
              if (landed.size) {
                setState(s =>
                  s.echoes.some(e => landed.has(e.clientMsgId))
                    ? { ...s, echoes: s.echoes.filter(e => !landed.has(e.clientMsgId)) }
                    : s,
                );
              }
            }
            if (msg.hasMore && page.length) {
              /* Old behaviour recursively requested the next-older page here,
                 walking the whole history back on every open. That is exactly
                 the unbounded hydration this card removes (4ynbWszGdqUd). We
                 stop at the window and surface hasOlder so the UI can offer an
                 explicit "Show earlier messages" (loadOlder) instead. */
              setState(s => (s.hasOlder ? s : { ...s, hasOlder: true }));
            } else if (!msg.hasMore) {
              setState(s => (s.hasOlder ? { ...s, hasOlder: false } : s));
            }
            break;
          }
          case 'ack':
            setState(s => ({ ...s, echoes: s.echoes.filter(e => e.clientMsgId !== msg.clientMsgId) }));
            break;
          case 'msg':
            appendMessage(msg.message);
            break;
          // The game round's card changed in place (reveal) or was taken out
          // (round abandoned by "next topic"), card Bkt2clm8SSOI. Same seq, so
          // the thread swaps the text without a second, competing card.
          case 'msg-update':
            setState(s => ({
              ...s,
              messages: s.messages.map(m => (m.seq === msg.message?.seq ? msg.message : m)),
            }));
            break;
          case 'msg-remove':
            setState(s => ({ ...s, messages: s.messages.filter(m => m.seq !== msg.seq) }));
            break;
          // A request row changed elsewhere in the room (kudos on completed
          // work, card HBYCnnD9q5FS). The panel owns its own fetch, so hand
          // it a signal rather than the data.
          case 'requests':
            window.dispatchEvent(new CustomEvent('pear:requests', { detail: msg }));
            break;
          // A chore row changed — a reaction landed or a done-note was
          // written (card MnorjNDwB3of). The Chores panel owns its fetch, so
          // hand it the signal, same shape as requests/game.
          case 'chores':
            window.dispatchEvent(new CustomEvent('pear:chores', { detail: msg }));
            break;
          // A decision moved (card HMzNgJETH7HE) — created, a vote landed, or
          // the two votes settled it. The frame carries the id only: what
          // anyone picked stays sealed server-side, so each client re-reads
          // its OWN projection of /api/decisions rather than trusting the
          // broadcast. Same hand-off as chores/game.
          case 'decisions':
            window.dispatchEvent(new CustomEvent('pear:decisions', { detail: msg }));
            break;
          // Same hand-off for the topic round: the panel fetches its own state.
          case 'game':
            window.dispatchEvent(new CustomEvent('pear:game', { detail: msg }));
            break;
          // Guess Me (card eq92fFXqQUuP) — same hand-off, its own event name:
          // the panel refetches its own round rather than trusting the frame.
          case 'guessme':
            window.dispatchEvent(new CustomEvent('pear:guessme', { detail: msg }));
            break;
          // Where Am I (card gzUOpwYknx7V) — one shared round, so a clue or a
          // guess in either client has to move the other panel.
          case 'whereami':
            window.dispatchEvent(new CustomEvent('pear:whereami', { detail: msg }));
            break;
          // A display name changed (card yFLCjm7ONIEc). The members list is
          // useSession's state, not this hook's — hand it the signal and let
          // it re-read /api/pair/status, same shape as requests/game.
          case 'members':
            window.dispatchEvent(new CustomEvent('pear:members', { detail: msg }));
            break;
          // The ceremonial pairing moment (card 2NuQ8dd2JMqs): the room's
          // broadcast the instant the second member joined. Hand it to
          // useSession, which owns the member list and the celebration.
          case 'paired':
            window.dispatchEvent(new CustomEvent('pear:paired', { detail: msg }));
            break;
          // Card J9hHjSZDFTCV — the room's own snapshot of who is watching.
          // Hand it out as a signal, same shape as requests/members: the header
          // owns the glow and knows the member list, this hook only owns the
          // socket.
          case 'presence':
            window.dispatchEvent(new CustomEvent('pear:presence', { detail: msg }));
            break;
          // Card tPPB3ozS4YrK — the partner started (or kept) typing. The room
          // only ever sends this to the OTHER member, and it carries no draft:
          // Thread owns the indicator and the member list, this hook only owns
          // the socket, so the frame is handed out as a signal like presence.
          case 'typing':
            window.dispatchEvent(new CustomEvent('pear:typing', { detail: msg }));
            break;
          // Nocturne game frames (card ut9aquEembR1) are transient — relayed
          // partner-only, never stored. The game opens its OWN socket
          // (games/nocturne/net.ts) so it can own its reconnect path; this
          // case only keeps the frame declared as handled, dispatched as a
          // window signal for anything else that wants to listen.
          case 'nocturne':
            window.dispatchEvent(new CustomEvent('pear:nocturne', { detail: msg }));
            break;
          case 'error':
            /* Card 4FFux4skKvYw: bad_token is the one failure a retry can
               never fix, so the queue is cleared with ONE explanation instead
               of being flushed into a socket that will keep refusing it. Any
               other error code leaves the queue alone. */
            if (msg.code === 'bad_token') {
              const dropped = queuedRef.current.length;
              if (dropped) {
                setState(s => ({ ...s, queued: [] }));
                reportActionFailure('keep your queued messages', {
                  message: `${dropped} queued message${dropped === 1 ? '' : 's'} could not be sent — your session expired. Sign in again.`,
                  ttlMs: 8000,
                });
              }
              invalidRef.current();
            }
            break;
        }
      };

      ws.onclose = () => {
        setState(s => ({ ...s, connected: false }));
        if (closed || !getToken()) return;
        retryRef.current = window.setTimeout(connect, 2000);
      };
    };

    connect();
    return () => {
      closed = true;
      if (retryRef.current) window.clearTimeout(retryRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
      setState(s => ({ ...s, connected: false }));
    };
  }, [token, appendMessage, flushQueue]);

  /* Card J9hHjSZDFTCV — visibility is a client-side fact the room cannot see,
     so both edges send it rather than waiting out the room's TTL:
       hidden  -> the partner's glow clears at once (no stale "together" on a
                  laptop that lid-closed ten minutes ago);
       visible -> the frame restores it, and a reconnect re-asserts the current
                  state because the fresh socket starts as "watching".
     The 30s ping is the heartbeat: it refreshes an entry without re-declaring
     visibility, so a tab left open for hours never ages out of the snapshot. */
  useEffect(() => {
    const say = (payload: Record<string, unknown>) => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ v: 1, ...payload }));
    };
    const onVisibility = () => say({ t: 'presence', hidden: document.hidden });
    const onReconnected = () => say({ t: 'presence', hidden: document.hidden });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pear:reconnected', onReconnected);
    onReconnected();
    const beat = window.setInterval(() => {
      if (!document.hidden) say({ t: 'ping' });
    }, 30_000);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pear:reconnected', onReconnected);
      window.clearInterval(beat);
    };
  }, []);

  return { ...state, send, retryQueued, removeMessage, typing, reset, loadOlder, search, loadToSeq, roleFor: roleForMember(members), nameFor: nameForMember(members), partnerName: partnerNameFrom(members), myMemberId: getMemberId() };
}

/** The partner's chat name, for copy that has to name them before any of
 *  their messages are on screen (the kudos sender says "For Jane", not
 *  "For your partner" — founder email 2026-10-28). */
function partnerNameFrom(members: Member[]) {
  const myId = getMemberId();
  return members.find(m => m.memberId !== myId)?.displayName ?? null;
}

function roleForMember(members: Member[]) {
  return (memberId: string | null | undefined): Role | null => {
    if (!memberId) return null;
    return members.find(m => m.memberId === memberId)?.role ?? null;
  };
}

/** The chat name for a sender (card yFLCjm7ONIEc) — the thread labels the
 *  partner's messages with whatever the display name is RIGHT NOW, so a
 *  change in Settings shows up on the next message with no reload. */
function nameForMember(members: Member[]) {
  return (memberId: string | null | undefined): string | null => {
    if (!memberId) return null;
    return members.find(m => m.memberId === memberId)?.displayName ?? null;
  };
}
